// src/pages/AllianceManagement/AllianceInstituteTab.js

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../../context/AuthContext';
import axiosInstance from '../../utils/axiosInstance';
import { API_URL } from '../../utils/runtimeConfig';
import './css/AllianceInstituteTab.css';

import NationSelector from '../../components/NationSelector';
import AllianceSelector from '../../components/AllianceSelector';
import { AppButton } from '../../components/ui';

const apiUrl = API_URL;

function createDefaultInstitute() {
  return {
    searchAndRescue: { level: '' },
    emergencyRoom: { level: '' },
    allianceGather: { level: '' },
    allianceBlitz: { level: '' },
    mineReset: { level: '' },
    nationalRecruitment: { level: '' },
    fortifyAttack: { level: '' },
    fortifyHP: { level: '' },
    fortifyDefence: { level: '' },
    allianceCannon: { level: '' },
    shieldAll: { level: '' },
    healthCare: { level: '' }
  };
}

const skillLabels = {
  searchAndRescue: 'Search and Rescue',
  emergencyRoom: 'Emergency Room',
  allianceGather: 'Alliance Gather',
  allianceBlitz: 'Alliance Blitz',
  mineReset: 'Mine Reset',
  nationalRecruitment: 'National Recruitment',
  fortifyAttack: 'Fortify Attack',
  fortifyHP: 'Fortify HP',
  fortifyDefence: 'Fortify Defense',
  allianceCannon: 'Alliance Cannon',
  shieldAll: 'Shield All',
  healthCare: 'Health Care'
};

const AllianceInstituteTab = () => {
  const { user } = useContext(AuthContext);

  const [nations, setNations] = useState([]);
  const [selectedNation, setSelectedNation] = useState(null);
  const [alliances, setAlliances] = useState([]);
  const [selectedAlliance, setSelectedAlliance] = useState(null);

  const [institute, setInstitute] = useState(createDefaultInstitute());

  useEffect(() => {
    if (!user) return;

    if (user.role === 'superUser') {
      fetchAllNations();
    }

    if (typeof user.nation === 'string') {
      fetchNationById(user.nation);
    } else if (user.nation && user.nation._id) {
      setSelectedNation(user.nation);
    }
  }, [user]);

  const fetchAllNations = async () => {
    try {
      const res = await axiosInstance.get(`${apiUrl}/nations`);
      setNations(res.data);
    } catch (err) {
      console.error('Error fetching nations:', err);
    }
  };

  const fetchNationById = async (nationId) => {
    try {
      const res = await axiosInstance.get(`${apiUrl}/nations/${nationId}`);
      setSelectedNation(res.data);
    } catch (error) {
      console.error('Error fetching nation for user:', error);
    }
  };

  useEffect(() => {
    if (selectedNation?._id) {
      fetchAlliances(selectedNation._id);
    }
  }, [selectedNation]);

  const fetchAlliances = async (nationId) => {
    try {
      const res = await axiosInstance.get(`${apiUrl}/nations/${nationId}/alliances`);
      setAlliances(res.data);
    } catch (err) {
      console.error('Error fetching alliances:', err);
    }
  };

  const fetchAllianceInstitute = useCallback(async (allianceId) => {
    try {
      const res = await axiosInstance.get(`${apiUrl}/alliances/${allianceId}`);
      if (res.data && res.data.institute) {
        mergeFetchedInstitute(res.data.institute);
      }
    } catch (err) {
      console.error('Error loading alliance institute:', err);
    }
  }, []);

  useEffect(() => {
    if (selectedAlliance?._id) {
      setInstitute(createDefaultInstitute());
      fetchAllianceInstitute(selectedAlliance._id);
    }
  }, [fetchAllianceInstitute, selectedAlliance]);

  const mergeFetchedInstitute = (fetched) => {
    const newInstitute = createDefaultInstitute();

    for (const skillKey of Object.keys(newInstitute)) {
      if (fetched[skillKey]) {
        newInstitute[skillKey].level =
          fetched[skillKey].level !== undefined
            ? String(fetched[skillKey].level)
            : '';
      }
    }

    setInstitute(newInstitute);
  };

  const handleInstituteLevelChange = (skillKey, newValue) => {
    setInstitute((prev) => ({
      ...prev,
      [skillKey]: {
        ...prev[skillKey],
        level: newValue
      }
    }));
  };

  const convertStringsToNumbers = (instituteObj) => {
    const result = createDefaultInstitute();

    for (const skillKey in instituteObj) {
      const strVal = instituteObj[skillKey].level;
      const parsed = parseInt(strVal, 10);
      const finalVal = isNaN(parsed) ? 0 : parsed;
      result[skillKey].level = finalVal;
    }

    return result;
  };

  const handleSaveInstitute = async () => {
    if (!selectedAlliance) return;

    try {
      const numericInstitute = convertStringsToNumbers(institute);

      await axiosInstance.put(`${apiUrl}/alliances/${selectedAlliance._id}/institute`, {
        institute: numericInstitute
      });

      alert('Alliance institute updated successfully!');
    } catch (error) {
      console.error('Error saving institute:', error);
      alert('Failed to save alliance institute.');
    }
  };

  const renderInstituteInputs = () => {
    const skillEntries = Object.entries(institute);

    return skillEntries.map(([skillKey, skillObj]) => (
      <div key={skillKey} className="am-institute-input-row">
        <label>{skillLabels[skillKey] || skillKey}</label>
        <input
          type="text"
          value={skillObj.level ?? ''}
          onChange={(e) => handleInstituteLevelChange(skillKey, e.target.value)}
        />
      </div>
    ));
  };

  if (user?.role === 'user') {
    return <p>You do not have permission to manage alliance institute.</p>;
  }

  return (
    <div className="am-institute-tab">
      <h2>Alliance Institute</h2>

      {user?.role === 'superUser' && (
        <NationSelector
          nations={nations}
          selectedNation={selectedNation}
          setSelectedNation={setSelectedNation}
        />
      )}

      <AllianceSelector
        alliances={alliances}
        selectedAlliance={selectedAlliance}
        setSelectedAlliance={setSelectedAlliance}
      />

      {selectedAlliance && (
        <>
          <h3>
            Editing Institute for {selectedAlliance.name} ({selectedAlliance.acronym})
          </h3>

          <div className="am-institute-inputs">{renderInstituteInputs()}</div>

          <AppButton className="am-institute-save-button" onClick={handleSaveInstitute}>
            Save Institute
          </AppButton>
        </>
      )}
    </div>
  );
};

export default AllianceInstituteTab;
